import { useCallback, useEffect, useState } from 'react';
import { useDataGridApi } from 'components/common/withDataGridApi';
import { debugLog } from 'shared/logger/debugLog';
import { DATA_GRID_EVENTS } from 'components/common/dataGrid/constants';
import {
  countActiveFilters,
  countFiltersExcludingField,
} from 'components/common/dataGrid/filters/fixedStateFilter';
import {
  GridFilterModel,
  GridPreferencePanelsValue,
} from '@mui/x-data-grid-pro';
import { UseDataGridFiltersIndicatorResult } from './types';

export function useDataGridFiltersIndicator(
  excludeField?: string
): UseDataGridFiltersIndicatorResult {
  const dataGridApiRef = useDataGridApi();
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let unsubscribe: (() => void) | undefined;

    const countFilters = (filterModel: GridFilterModel | undefined): number => {
      if (!filterModel) return 0;
      if (excludeField) {
        return countFiltersExcludingField(filterModel, excludeField);
      }
      return countActiveFilters(filterModel);
    };

    const setupSubscription: () => void = () => {
      if (!dataGridApiRef?.current?.state) {
        timeoutId = setTimeout(setupSubscription, 100);
        return;
      }

      try {
        const filterModel = dataGridApiRef.current.state.filter?.filterModel;
        setActiveFiltersCount(countFilters(filterModel));

        unsubscribe = dataGridApiRef.current.subscribeEvent(
          DATA_GRID_EVENTS.FILTER_MODEL_CHANGE,
          (params) => {
            setActiveFiltersCount(countFilters(params as GridFilterModel));
          }
        );
      } catch (error) {
        debugLog('Failed to setup filter subscription:', error);
        timeoutId = setTimeout(setupSubscription, 100);
      }
    };

    setupSubscription();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
    };
  }, [dataGridApiRef, excludeField]);

  const isFiltersPanelOpenInGrid = useCallback((): boolean => {
    const preferencePanel = dataGridApiRef?.current?.state?.preferencePanel;
    return (
      preferencePanel?.open === true &&
      preferencePanel?.openedPanelValue === GridPreferencePanelsValue.filters
    );
  }, [dataGridApiRef]);

  const onToggleFilters = useCallback((wasOpenAtInteraction?: boolean) => {
    if (!dataGridApiRef?.current) return;

    const isPanelOpen =
      typeof wasOpenAtInteraction === 'boolean'
        ? wasOpenAtInteraction
        : isFiltersPanelOpenInGrid();

    if (isPanelOpen) {
      dataGridApiRef.current.hidePreferences?.();
    } else {
      dataGridApiRef.current.showPreferences?.(GridPreferencePanelsValue.filters);
    }
  }, [dataGridApiRef, isFiltersPanelOpenInGrid]);

  return {
    activeFiltersCount,
    onToggleFilters,
    isFiltersPanelOpenInGrid,
  };
}
